import { FaireOncall } from '@faire/web-api--source/faire/logging/FaireOncall';
import { FairePillar } from '@faire/web-api--source/faire/logging/FairePillar';
import { FairePod } from '@faire/web-api--source/faire/logging/FairePod';

export namespace FairePodExtensions {
  export const getDeprecated = (enumValue: FairePod): boolean | undefined => {
    return FairePodRecord[enumValue]?.deprecated;
  };
  export const getExcludeJira = (enumValue: FairePod): boolean | undefined => {
    return FairePodRecord[enumValue]?.excludeJira;
  };
  export const getPillar = (
    enumValue: FairePod
  ): keyof typeof FairePillar | undefined => {
    return FairePodRecord[enumValue]?.pillar;
  };
  export const getOncall = (
    enumValue: FairePod
  ): keyof typeof FaireOncall | undefined => {
    return FairePodRecord[enumValue]?.oncall;
  };
  export const getManagerEmail = (enumValue: FairePod): string | undefined => {
    return FairePodRecord[enumValue]?.managerEmail;
  };
  export const getSlackChannelOverride = (
    enumValue: FairePod
  ): string | undefined => {
    return FairePodRecord[enumValue]?.slackChannelOverride;
  };
  export const getJiraNameOverride = (
    enumValue: FairePod
  ): string | undefined => {
    return FairePodRecord[enumValue]?.jiraNameOverride;
  };

  const FairePodRecord: Record<
    FairePod,
    {
      deprecated?: boolean;
      excludeJira?: boolean;
      pillar?: keyof typeof FairePillar;
      oncall?: keyof typeof FaireOncall;
      managerEmail?: string;
      slackChannelOverride?: string;
      jiraNameOverride?: string;
    }
  > = {
    FAIRE_POD_UNKNOWN: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_ANTI_ABUSE: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_ANTI_ABUSE`,
      managerEmail: `jevin@faire.com`,
      slackChannelOverride: `anti-abuse-alerts`,
    },
    FAIRE_POD_APPAREL: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_APPAREL_BRAND: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_APPAREL_EVENTS: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_APPAREL_PREORDER: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_APPAREL_RETAILER: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_ACQUISITION: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      slackChannelOverride: `pod-brand-acquisition-alerts`,
      managerEmail: `preshoth.paramalingam@faire.com`,
    },
    FAIRE_POD_BRAND_GROWTH: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_MARKETPLACE_QUALITY: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_ONBOARDING: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_BRAND_ORDER_MANAGEMENT: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_BRAND_FAIRE_DIRECT: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_CATEGORY_INCUBATION: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_DATA: {
      excludeJira: true,
      pillar: `FAIRE_PILLAR_DATA`,
      slackChannelOverride: `data-alerts`,
    },
    FAIRE_POD_DISCOVERY_AI: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `tom.ma@faire.com`,
    },
    FAIRE_POD_DISCOVERY_AE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `samir.durrani@faire.com`,
    },
    FAIRE_POD_DISCOVERY_LOGGING: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      slackChannelOverride: `disco-logging-jira`,
      managerEmail: `elijah.flesher@faire.com`,
    },
    FAIRE_POD_FAIRE_FOUNDATIONS: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_PLATFORM`,
      managerEmail: `dale.hamill@faire.com`,
    },
    FAIRE_POD_INTEGRATIONS: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_INTERNAL_TOOLS: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_PLATFORM`,
    },
    FAIRE_POD_INTERNATIONAL: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_INTERNATIONAL_BRAND_GROWTH: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_INTERNATIONAL_BRAND: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_INTERNATIONAL`,
      managerEmail: `gray@faire.com`,
    },
    FAIRE_POD_INTERNATIONAL_LAUNCH: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_INTERNATIONAL_PLATFORM: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_INTERNATIONAL`,
    },
    FAIRE_POD_MARKETS: {
      pillar: `FAIRE_PILLAR_RETAILER`,
      oncall: `FAIRE_ONCALL_RETAILER_ORDER_INCENTIVES`,
      managerEmail: `julian.konomi@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_PRICING: {
      pillar: `FAIRE_PILLAR_RETAILER_TOOLS_AND_PROGRAMS`,
      oncall: `FAIRE_ONCALL_RETAILER_TOOLS_AND_PROGRAMS`,
      managerEmail: `alexandre.bisson@faire.com`,
      slackChannelOverride: `pod-retailer-pricing-alerts`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_SHIPPING: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `alexandre.bisson@faire.com`,
    },
    FAIRE_POD_MARKETPLACE_QUALITY: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `gaurav.saraf@faire.com`,
      slackChannelOverride: `pod-marketplace-quality-alerts`,
    },
    FAIRE_POD_MOBILE: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_MOBILE_STRIKE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `raissa.nucci@faire.com`,
    },
    FAIRE_POD_MOBILE_CORE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_RETAILER`,
    },
    FAIRE_POD_MOBILE_PLATFORM: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      managerEmail: `ragan.vadlamudi@faire.com`,
      slackChannelOverride: `pod-mobile-platform-notifications`,
    },
    FAIRE_POD_MOBILE_EXPANSION: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_MOBILE_INTERNATIONAL: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_INTERNATIONAL`,
      oncall: `FAIRE_ONCALL_INTERNATIONAL`,
      managerEmail: `ani.borah@faire.com`,
    },
    FAIRE_POD_MULTI_SERVICE: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_PLATFORM: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_PLATFORM`,
      managerEmail: `ryan@faire.com`,
      slackChannelOverride: `pod-platform-updates`,
    },
    FAIRE_POD_PLATFORM_CONTROL: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `cheuk-man.kong@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_PLATFORM_SCALE: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `felix.reyn@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_PLATFORM_RELIABILITY: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `felix.reyn@faire.com`,
    },
    FAIRE_POD_PLATFORM_PERFORMANCE_AND_INSIGHTS: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `khaled.said@faire.com`,
    },
    FAIRE_POD_PLATFORM_INFRASTRUCTURE_AND_RESILIENCE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `felix.reyn@faire.com`,
    },
    FAIRE_POD_PLATFORM_INFRASTRUCTURE_AND_RESILIENCY: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `khaled.said@faire.com`,
    },
    FAIRE_POD_PLATFORM_TAP: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_INFRASTRUCTURE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `mark.widish@faire.com`,
    },
    FAIRE_POD_PREORDERS: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_PRICING: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
    },
    FAIRE_POD_BRAND_PRICING: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_QUALITY: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `bryan.holder@faire.com`,
    },
    FAIRE_POD_RISK: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_SECURITY: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_SECURITY`,
      managerEmail: `bryan.holder@faire.com`,
    },
    FAIRE_POD_RETAILER_LIFECYCLE: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_CONFIDENCE: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_GROWTH: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
    },
    FAIRE_POD_RETAILER_ACTIVATION: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `luthfur.chowdhury@faire.com`,
    },
    FAIRE_POD_RETAILER_ONBOARDING: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `luthfur.chowdhury@faire.com`,
      slackChannelOverride: `pod-retailer-onboarding-notifications`,
    },
    FAIRE_POD_RETAILER_DISCOVERY: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_INSIDER: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `howard.pang@faire.com`,
      slackChannelOverride: `pod-insider-sla`,
    },
    FAIRE_POD_UNASSIGNED: {
      excludeJira: true,
      pillar: `FAIRE_PILLAR_UNASSIGNED`,
      oncall: `FAIRE_ONCALL_UNASSIGNED`,
    },
    FAIRE_POD_BRAND_UNASSIGNED: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      slackChannelOverride: `pillar-brand-eng-sla`,
      managerEmail: `mark.widish@faire.com`,
    },
    FAIRE_POD_WEB_PLATFORM: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      managerEmail: `ragan.vadlamudi@faire.com`,
    },
    FAIRE_POD_CLIENT_TOOLS: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_UNDERWRITING: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_ORDER_WRITING: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_LOCALIZATION: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_MARKETPLACE_SUCCESS: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_STABLE_STAPLES: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_FINANCIAL_PRODUCTS: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `howard.pang@faire.com`,
      slackChannelOverride: `pod-financial-products-sla`,
    },
    FAIRE_POD_UPMARKET: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_INSPIRE: {
      pillar: `FAIRE_PILLAR_RETAILER`,
      managerEmail: `dmytro.landberg@faire.com`,
      slackChannelOverride: `pod-retailer-inspire-notifications`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_SEARCH: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      managerEmail: `eric.hacke@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_CHECKOUT: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_ACQUISITION: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `pavlo.kuzhel@faire.com`,
      slackChannelOverride: `pod-acquisition`,
    },
    FAIRE_POD_RETAILER_EVALUATE: {
      pillar: `FAIRE_PILLAR_RETAILER`,
      oncall: `FAIRE_ONCALL_RETAILER_ORDER_INCENTIVES`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_FAIRE_DIRECT_REWARDS: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_FAIRE_DIRECT_CRM: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_FAIRE_DIRECT_PROSPECTING: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_APPAREL_INTEGRATIONS: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_EXPANSION`,
      managerEmail: `cedo.segulja@faire.com`,
    },
    FAIRE_POD_BRAND_F30D_LIFECYCLE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_INTERNATIONAL_TRANSLATION: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_INTERNATIONAL`,
      oncall: `FAIRE_ONCALL_INTERNATIONAL`,
      managerEmail: `dave.collie@faire.com`,
    },
    FAIRE_POD_INVOICING_AND_SUPPORT_EXPERIENCE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_INTERNATIONAL`,
      oncall: `FAIRE_ONCALL_INTERNATIONAL`,
      managerEmail: `ryan.orr@faire.com`,
      slackChannelOverride: `pod-invoicing-support-experience`,
    },
    FAIRE_POD_VAT_INTL_CX: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_INTERNATIONAL_CX: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_XB_SHIPPING: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_SHIPPING_EXPERIENCE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_RETAILER_INFRASTRUCTURE: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_TAXONOMY: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      managerEmail: `eliot.daigneaultmestre@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_FOOD_AND_BEVERAGE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `austin.dobrik@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_UPMARKET_GROWTH: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_UPMARKET_EXPERIENCE: {
      pillar: `FAIRE_PILLAR_EXPANSION`,
      managerEmail: `howard.pang@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_FREIGHT_MARGIN: {
      pillar: `FAIRE_PILLAR_INTERNATIONAL`,
      managerEmail: `ani.borah@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_REORDER: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_ENTERPRISE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_SLATE_DESIGN_SYSTEM: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      managerEmail: `ragan.vadlamudi@faire.com`,
      slackChannelOverride: `pod-slate`,
    },
    FAIRE_POD_SEARCH_INFRASTRUCTURE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `eliot.daigneaultmestre@faire.com`,
      slackChannelOverride: `pod-discovery-infra-enablement`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_CART_SERVICE: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_UPMARKET_TEAM_MANAGEMENT: {
      pillar: `FAIRE_PILLAR_RETAILER`,
      oncall: `FAIRE_ONCALL_RETAILER_ORDER_INCENTIVES`,
      managerEmail: `howard.pang@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_UPMARKET_TOOLING: {
      pillar: `FAIRE_PILLAR_RETAILER_TOOLS_AND_PROGRAMS`,
      oncall: `FAIRE_ONCALL_RETAILER_TOOLS_AND_PROGRAMS`,
      managerEmail: `mark.fennema@faire.com`,
      slackChannelOverride: `pod-upmarket-tooling-sla`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETENTION_EXPERIENCE: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_SEO: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_SERVICE_STANDARDS: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_SUPPLY_INTEGRITY: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_RETAILER_SUCCESS: {
      pillar: `FAIRE_PILLAR_RETAILER`,
      oncall: `FAIRE_ONCALL_RETAILER_ORDER_INCENTIVES`,
      managerEmail: `thiago.pilon@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_THEMATIC_DISCOVERY: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_TAXONOMY: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `eliot.daigneaultmestre@faire.com`,
      slackChannelOverride: `pod-discovery-infra-enablement`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_SEARCH_EXPERIENCE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `austin.dobrik@faire.com`,
      slackChannelOverride: `pod-evaluate-and-order-notifications`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_INSPIRE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `dmytro.landberg@faire.com`,
      slackChannelOverride: `pod-discovery-inspire-eng`,
    },
    FAIRE_POD_SEARCH_RANKING_ALGORITHMS: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `jason.xu@faire.com`,
      slackChannelOverride: `search-algo-alerts`,
    },
    FAIRE_POD_DISCOVERY_PAID_PLACEMENT: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `ani.borah@faire.com`,
      slackChannelOverride: `pod-discovery-paid-placement-sla`,
    },
    FAIRE_POD_GROWTH_MOBILE: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `luthfur.chowdhury@faire.com`,
      slackChannelOverride: `retailer-growth-mobile-sla`,
    },
    FAIRE_POD_COMMUNICATIONS_PLATFORM: {
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_OPEN_WITH_FAIRE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
    },
    FAIRE_POD_COMMISSION: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_FAIRE_DIRECT_GROWTH: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `riley.davidson@faire.com`,
      slackChannelOverride: `pod-faire-direct-growth-notifications`,
    },
    FAIRE_POD_SHOPIFY: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_RETAILER_POS_INTEGRATIONS: {
      pillar: `FAIRE_PILLAR_RETAILER`,
      oncall: `FAIRE_ONCALL_INTEGRATIONS`,
      managerEmail: `howard.pang@faire.com`,
      slackChannelOverride: `pod-retailer-pos-integrations-sla`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_TOFU: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `mario.hebert@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BROWSE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `raissa.nucci@faire.com`,
      slackChannelOverride: `pod-browse-sla`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_DATA_TEAM_FINANCIAL_PRODUCTS: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_ANTI_ABUSE`,
      managerEmail: `georgii@faire.com`,
      slackChannelOverride: `data-team-financial-products`,
    },
    FAIRE_POD_RECOMMENDATIONS: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `dmytro.landberg@faire.com`,
      slackChannelOverride: `pod-discovery-recommendations`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_SEARCH_EXPERIENCE_ACCELERATOR: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      deprecated: true,
      excludeJira: true,
      managerEmail: `austin.dobrik@faire.com`,
    },
    FAIRE_POD_INTERNATIONAL_FOUNDATIONS: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_BRAND`,
    },
    FAIRE_POD_SUPPORT_EXPERIENCE: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_RETAILER_TOOLS_AND_PROGRAMS`,
      oncall: `FAIRE_ONCALL_RETAILER_TOOLS_AND_PROGRAMS`,
      managerEmail: `matthew.d-cruz@faire.com`,
    },
    FAIRE_POD_BRAND_CATALOG_COMPLETENESS: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `donald.ho@faire.com`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_GROWTH_PLATFORM: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `cheryl.lau@faire.com`,
    },
    FAIRE_POD_RETAILER_ORDER_PLACEMENT: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `neel.hajare@faire.com`,
      slackChannelOverride: `pod-retailer-order-placement-sla`,
    },
    FAIRE_POD_RETAILER_ORDER_MANAGEMENT: {
      pillar: `FAIRE_PILLAR_RETAILER_TOOLS_AND_PROGRAMS`,
      oncall: `FAIRE_ONCALL_RETAILER_TOOLS_AND_PROGRAMS`,
      managerEmail: `matthew.d-cruz@faire.com`,
      jiraNameOverride: `Retailer POS Integrations`,
      slackChannelOverride: `pod-retailer-order-management-sla`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_OPERATIONS_MANAGEMENT: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `preshoth.paramalingam@faire.com`,
      jiraNameOverride: `BOM`,
      slackChannelOverride: `pod-bom`,
    },
    FAIRE_POD_BRAND_PRICING_AND_REORDERS: {
      pillar: `FAIRE_PILLAR_BRAND`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_CATALOG_COMPLETENESS_AND_BRAND_PROGRAMS: {
      pillar: `FAIRE_PILLAR_BRAND`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_BRAND_LISTING_QUALITY_AND_PRODUCT_ATTRIBUTES: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `tim.yangtuo@faire.com`,
      slackChannelOverride: `pod-listing-quality-product-attributes`,
    },
    FAIRE_POD_DISCOVERY_INFRA_ENABLEMENT: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `xiannian.fan@faire.com`,
    },
    FAIRE_POD_EXPERIMENT_PLATFORM: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_DATA_SCIENCE_ML_PLATFORM`,
      managerEmail: `michael.tompkins@faire.com`,
    },
    FAIRE_POD_KNOWLEDGE_GRAPH: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_DATA_SCIENCE_ML_PLATFORM`,
      managerEmail: `michael.tompkins@faire.com`,
    },
    FAIRE_POD_PLATFORM_PRODUCTIVITY: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_PLATFORM`,
      managerEmail: `cheuk-man.kong@faire.com`,
      jiraNameOverride: `Platform Productivity`,
      slackChannelOverride: `pod-platform-productivity`,
    },
    FAIRE_POD_DISCOVERY_PRODUCT_PLATFORM: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `dmytro.landberg@faire.com`,
    },
    FAIRE_POD_PERSONALIZATION: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `yuanchi.ning@faire.com`,
      slackChannelOverride: `personalization-team`,
    },
    FAIRE_POD_BRAND_CATALOG_GROWTH: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `ryan.closner@faire.com`,
    },
    FAIRE_POD_SEARCH_QUERY_UNDERSTANDING_AND_RETRIEVAL: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `jason.xu@faire.com`,
      slackChannelOverride: `search-algo-alerts`,
    },
    FAIRE_POD_CORE_DATA_INFRA: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_DATA_SCIENCE_ML_PLATFORM`,
      managerEmail: `michael.tompkins@faire.com`,
    },
    FAIRE_POD_PP_ADS_MANAGER: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `ani.borah@faire.com`,
      slackChannelOverride: `pod-discovery-paid-placement-sla`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_PP_ADS_DELIVERY: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `ani.borah@faire.com`,
      slackChannelOverride: `pod-discovery-paid-placement-sla`,
      deprecated: true,
      excludeJira: true,
    },
    FAIRE_POD_RETAILER_GROWTH_DATA_SCIENCE: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `robby.zeller@faire.com`,
    },
    FAIRE_POD_BRAND_DATA_SCIENCE: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `robby.zeller@faire.com`,
    },
    FAIRE_POD_DATA_TEAM_INSIDER_AND_SHIPPING_COSTS: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `jevin@faire.com`,
    },
    FAIRE_POD_VALUE_MOBILE: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `neel.hajare@faire.com`,
    },
    FAIRE_POD_AI_PLATFORM: {
      pillar: `FAIRE_PILLAR_EPDD_FOUNDATIONS`,
      oncall: `FAIRE_ONCALL_DATA_SCIENCE_ML_PLATFORM`,
      managerEmail: `namit.katariya@faire.com`,
      slackChannelOverride: `data-alerts-ml-platform`,
    },
    FAIRE_POD_OPS_ENABLEMENT: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `matthew.d-cruz@faire.com`,
    },
    FAIRE_POD_EVALUATE: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `austin.dobrik@faire.com`,
      jiraNameOverride: `Evaluate & Order`,
      slackChannelOverride: `pod-evaluate-and-order-notifications`,
    },
    FAIRE_POD_ADS: {
      deprecated: true,
      excludeJira: true,
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `ani.borah@faire.com`,
      slackChannelOverride: `pod-discovery-paid-placement-sla`,
    },
    FAIRE_POD_ADS_DELIVERY: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `ani.borah@faire.com`,
      slackChannelOverride: `pod-discovery-paid-placement-sla`,
    },
    FAIRE_POD_ADS_MANAGER: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `ani.borah@faire.com`,
      slackChannelOverride: `pod-discovery-paid-placement-sla`,
    },
    FAIRE_POD_BRAND_PLATFORM: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `mark.widish@faire.com`,
    },
    FAIRE_POD_BRAND_PLATFORM_COMPLIANCE: {
      pillar: `FAIRE_PILLAR_BRAND`,
      oncall: `FAIRE_ONCALL_BRAND`,
      managerEmail: `ryan.closner@faire.com`,
    },
    FAIRE_POD_ORDERS_MIGRATION: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `neel.hajare@faire.com`,
      slackChannelOverride: `service-orders`,
    },
    FAIRE_POD_FAIRE_PAY: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `howard.pang@faire.com`,
    },
    FAIRE_POD_ONE_STOP_SHOP: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `matthew.d-cruz@faire.com`,
      slackChannelOverride: `pod-one-stop-shop-sla`,
    },
    FAIRE_POD_MARKETS_COORDINATION: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `mark.fennema@faire.com`,
    },
    FAIRE_POD_FULFILLED_BY_FAIRE: {
      pillar: `FAIRE_PILLAR_VALUE`,
      oncall: `FAIRE_ONCALL_VALUE`,
      managerEmail: `alexandre.bisson@faire.com`,
      slackChannelOverride: `pod-fbf-sla`,
    },
    FAIRE_POD_SEARCH_UX: {
      pillar: `FAIRE_PILLAR_DISCOVERY`,
      oncall: `FAIRE_ONCALL_DISCOVERY`,
      managerEmail: `raissa.nucci@faire.com`,
      slackChannelOverride: `pod-search-ux-sla`,
    },
    FAIRE_POD_RELATIONSHIP_GROWTH: {
      pillar: `FAIRE_PILLAR_RETAILER_GROWTH`,
      oncall: `FAIRE_ONCALL_RETAILER_GROWTH`,
      managerEmail: `riley.davidson@faire.com`,
      slackChannelOverride: `pod-relationship-growth-notifications`,
    },
  };
}
